/*
 * COPYRIGHT (c) 2024
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */
import React, { useContext, useMemo } from 'react';
import { TreeTable } from '@scuf/datatable';
import { Input } from '@scuf/common';
import { useTranslation } from '@scuf/localization';

import { generateTreeNodesforUtilityBillBaseline } from '../UtilityBillBaselineUtils';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import descriptors from '../../../../locales/en-US/descriptors';
import { ApiContext } from '../../../../context/ApiContext';
import { IUtilityMeterBaselineStatus } from '../../BaselineConfigurationModels';

import './UtilityMetersTableTheme.scss';

interface IUtilityMetersTableProps {
  meterList: IUtilityMeterBaselineStatus[];
}

export const UtilityMetersBaselineTable: React.FC<IUtilityMetersTableProps> = (
  props: IUtilityMetersTableProps
) => {
  const baselineDesc = descriptors.baseline;
  const [t] = useTranslation();
  const { meterList } = props;

  const apiContext = useContext(ApiContext);
  const { utilityBillBaselineActionClick } = apiContext!;

  const [searchText, setSearchText] = React.useState('');

  const [dataPoints, totalFilteredMeters] = useMemo(() => {
    const _filter = meterList.filter(
      (meter) =>
        meter.meterName.toLowerCase().includes(searchText.toLowerCase()) ||
        (meter.baselineDetails &&
          meter.baselineDetails.quality
            .toLowerCase()
            .includes(searchText.toLowerCase()))
    );
    return [
      generateTreeNodesforUtilityBillBaseline(
        _filter,
        ['electricity'],
        utilityBillBaselineActionClick!
      ),
      _filter.length
    ];
  }, [meterList, searchText]);

  return (
    <div className="utility-meters-table-wrapper">
      <div className="search-wrapper">
        <Input
          className="utility-meter-search-box"
          placeholder={t(baselineDesc.searchPlaceholder)}
          search={true}
          fluid={true}
          value={searchText}
          onChange={(val) => setSearchText(val)}
        />
        <div className="total-count">
          {meterList.length > 0
            ? `${totalFilteredMeters} ${t(baselineDesc.total)}`
            : ''}
        </div>
      </div>
      <TreeTable data={[...dataPoints]}>
        <TreeTable.Column field="name" header="Name" sortable={true} />
        <TreeTable.Column
          field="lastTrained"
          header="Last Trained"
          sortable={true}
        />
        <TreeTable.Column
          field="baselineQuality"
          header="Baseline Quality"
          sortable={true}
        />
        <TreeTable.Column field="actions" header="Actions" />
      </TreeTable>
    </div>
  );
};

export const UtilityMetersBaselineTableWithLoader = withLoadingStatus(
  withErrorStatus(UtilityMetersBaselineTable)
);
